import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Dropdown, Space, Tag } from "antd";
import { DotVerticalIcon } from "assets/icons";

import AppButton from "../../../components/button";
import ModalForm from "../../../components/forms/components/modalForm";
import AppTable from "../../../components/table";
import {
  CHANNEL_NAME,
  CHANNEL_PROVIDER,
  CHANNEL_STATUS,
  STATUS_TYPE,
  USER_TYPE,
} from "../../../constants/appConstant";
import { AuthInfo, doGetCreditBalance } from "../../../stores/slices/auth";
import {
  doDeleteChannel,
  doGetChannelPage,
  numOfEls,
} from "../../../stores/slices/channel";
import { setHeaderTitle, setIsRefresh } from "../../../stores/slices/layout";

import ChannelForm from "./channelForm";

const ChannelPage = () => {
  const { t } = useTranslation();
  const params = useParams();

  const dispatch = useDispatch();
  const authInfo = useSelector(AuthInfo);
  const noOfElements = useSelector(numOfEls);

  const [columns, setColumns] = useState([]);
  const [isView, setIsView] = useState(false);
  const [isOpenFrm, setIsOpenFrm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemSelected, setItemSelected] = useState(null);

  const [loadingList, setLoadingList] = useState(false);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [selectedChanel, setSelectedChanel] = useState(false);

  const [searchParam, setSearchParam] = useState({
    pageNumber: 1,
    pageSize: 10,
    channelStatuses: [CHANNEL_STATUS.ENABLED, CHANNEL_STATUS.DISABLED],
    sortDirection: "DESC",
    sortFieldChannel: "createdTime",
  });

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {};
    filters.name &&
      (newSearchParam = {
        ...newSearchParam,
        channelNames: filters.name,
      });
    filters.apiKey &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyApiKey: filters.apiKey[0],
      });
    filters.workspaceName &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyWorkspaceName: filters.workspaceName[0],
      });
    filters.provider &&
      filters.provider.indexOf(CHANNEL_PROVIDER.SMS) !== -1 &&
      (newSearchParam = { ...newSearchParam, sms: true });
    filters.provider &&
      filters.provider.indexOf(CHANNEL_PROVIDER.MMS) !== -1 &&
      (newSearchParam = { ...newSearchParam, mms: true });
    filters.provider &&
      filters.provider.indexOf(CHANNEL_PROVIDER.WhatsApp) !== -1 &&
      (newSearchParam = { ...newSearchParam, whatsapp: true });
    filters.provider &&
      filters.provider.indexOf(CHANNEL_PROVIDER.Voice) !== -1 &&
      (newSearchParam = { ...newSearchParam, voice: true });

    newSearchParam = {
      ...newSearchParam,
      channelStatuses: filters.status
        ? filters.status
        : [CHANNEL_STATUS.ENABLED, CHANNEL_STATUS.DISABLED],
    };

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortFieldChannel: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortFieldChannel: "createdTime",
        sortDirection: "DESC",
      };
    }

    newSearchParam = {
      ...newSearchParam,
      workspaceId:
        params.id !== "all"
          ? params.id === "default"
            ? authInfo.workspaceId
            : params.id
          : null,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };
    setSearchParam(newSearchParam);
  };

  const actionOptionItems = record => {
    return [
      {
        key: "1",
        label: <span>{t("channel.viewAndEdit")}</span>,
        icon: (
          <span style={{ marginInlineEnd: 8 }}>
            <EyeOutlined />
          </span>
        ),
        disabled: record.status === STATUS_TYPE.DELETE,
        onClick: () => {
          setIsView(true);
          setItemSelected(record);
          setIsOpenFrm(true);
        },
      },
      {
        key: "2",
        danger: true,
        label: <span>{t("channel.delete")}</span>,
        icon: (
          <span style={{ marginInlineEnd: 8 }}>
            <DeleteOutlined />
          </span>
        ),
        onClick: () => {
          setSelectedChanel(record);
          setIsOpenModalDelete(true);
        },
      },
    ];
  };

  const handleGetChannelPageAndCreditBalance = async () => {
    const { payload } = await dispatch(doGetChannelPage(searchParam));
    if (payload?.content?.length > 0) {
      const list = payload?.content;
      let newList = list.map(async channel => {
        const { payload } = await dispatch(
          doGetCreditBalance({ apiKey: channel && channel.apiKey })
        );
        if (payload && payload.status === 200) {
          return {
            ...channel,
            creditBalance: payload && payload.data,
          };
        }
      });
      newList = await Promise.all(newList);
      return { ...payload, content: newList };
    }
  };

  const generateTableColumns = () => {
    let cols = [
      {
        title: t("label.channel"),
        width: 80,
        dataIndex: "name",
        key: "name",
        sorter: true,
        filters: [
          {
            text: t("channelName.BULKSMS"),
            value: CHANNEL_NAME.BULKSMS,
          },
        ],
        render: (_, { name }) => (
          <>{name === CHANNEL_NAME.BULKSMS ? t("channelName.BULKSMS") : name}</>
        ),
      },

      {
        title: t("channel.provides"),
        width: 100,
        dataIndex: "provider",
        key: "provider",
        filters: [
          {
            text: t("channelProvider.sms"),
            value: CHANNEL_PROVIDER.SMS,
          },
          {
            text: t("channelProvider.mms"),
            value: CHANNEL_PROVIDER.MMS,
          },
          {
            text: t("channelProvider.whatsapp"),
            value: CHANNEL_PROVIDER.WhatsApp,
          },
          {
            text: t("channelProvider.voice"),
            value: CHANNEL_PROVIDER.Voice,
          },
        ],
        ellipsis: {
          showTitle: true,
        },
        render: (_, { provider }) => (
          <>
            {Object.entries(provider)
              ?.filter(([key]) => key)
              ?.map(([key]) => {
                return t("channelProvider." + key);
              })
              .join(", ")}
          </>
        ),
      },
      {
        title: t("channel.creditBalance"),
        width: 50,
        dataIndex: "creditBalance",
        key: "creditBalance",
        render: (_, { creditBalance }) => {
          return <>{creditBalance ? creditBalance : "-"}</>;
        },
      },
      {
        title: t("channel.status"),
        width: 60,
        dataIndex: "status",
        key: "status",
        filters: [
          {
            text: t("channelStatus.ENABLED"),
            value: CHANNEL_STATUS.ENABLED,
          },
          {
            text: t("channelStatus.DISABLED"),
            value: CHANNEL_STATUS.DISABLED,
          },
        ],
        render: (_, { status }) => (
          <>
            <Tag
              color={
                status === CHANNEL_STATUS.ENABLED
                  ? "#0E9154"
                  : status === CHANNEL_STATUS.DISABLED
                  ? "gold"
                  : "#E01839"
              }
              bordered={false}
            >
              {t("channelStatus." + status)}
            </Tag>
          </>
        ),
      },
      {
        title: t("channel.actions"),
        width: 40,
        render: (_, record) => {
          return (
            <>
              <Space size="small">
                <Dropdown
                  menu={{
                    items: actionOptionItems(record),
                  }}
                  overlayClassName="workspaceActionDropdown"
                  placement="bottomRight"
                  trigger={"click"}
                >
                  <Button
                    type="text"
                    shape="circle"
                    size="middle"
                    icon={<DotVerticalIcon />}
                  />
                </Dropdown>
              </Space>
            </>
          );
        },
      },
    ];
    if (authInfo?.type === USER_TYPE.SUPER_ADMIN) {
      cols = [
        {
          title: t("channel.workspace"),
          width: 120,
          dataIndex: "workspaceName",
          key: "workspaceName",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("label.workspaceName").toString().toLowerCase(),
          ellipsis: {
            showTitle: true,
          },
          render: (_, { workspace }) => <>{workspace?.name || "-"}</>,
        },
        ...cols,
      ];
    }
    setColumns(cols);
  };

  const handleDelete = async record => {
    const { payload } = await dispatch(doDeleteChannel(record.id.toString()));
    setIsOpenModalDelete(false);
    if (searchParam.pageNumber > 1 && noOfElements === 1) {
      const newSearchParam = {
        ...searchParam,
        pageNumber: searchParam.pageNumber - 1,
      };
      setSearchParam(newSearchParam);
    }
    dispatch(setIsRefresh(uuid()));
    setIsDeleting(false);
    return payload;
  };

  useEffect(() => {
    dispatch(setHeaderTitle(["channelManagement"]));
  }, [authInfo]);

  useEffect(() => {
    generateTableColumns();
  }, [noOfElements]);

  const handleTitleDelete = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <QuestionCircleOutlined style={{ color: "red", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("channel.deleteChannelTitle")}
        </span>
      </div>
    );
  };

  return (
    <div style={channelStyles.mainContainer}>
      <div style={channelStyles.buttonContainer}>
        <AppButton
          label={t("button.add")}
          color={"primary"}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsView(false);
            setItemSelected(null);
            setIsOpenFrm(true);
          }}
          disabled={
            authInfo.type === USER_TYPE.USER &&
            (!authInfo.workspaceRole ||
              authInfo.workspaceRole === USER_TYPE.USER)
          }
          style={{
            marginLeft: "auto",
            minWidth: "auto",
          }}
        />
      </div>

      {columns && (
        <AppTable
          searchParam={searchParam}
          // api={doGetChannelPage(searchParam)}
          handleSearch={handleSearch}
          handleTableChange={handleTableChange}
          handleFilter={handleFilter}
          columns={columns}
          setSearchParam={setSearchParam}
          onCustomFetchData={handleGetChannelPageAndCreditBalance}
        />
      )}

      <ModalForm
        title={
          !itemSelected
            ? t("channel.addChannel")
            : isView
            ? t("channel.viewChannel")
            : t("channel.updateChannel")
        }
        onCancel={() => setIsOpenFrm(false)}
        destroyOnClose={true}
        isOpen={isOpenFrm}
        customWidth={500}
        footer={false}
        maskClosable={false}
      >
        <ChannelForm
          isViewFrm={isView}
          itemSelected={itemSelected}
          onCancel={() => setIsOpenFrm(false)}
          onSuccess={() => {
            dispatch(setIsRefresh(uuid()));
            setIsOpenFrm(false);
          }}
          userType={authInfo.type}
          loadingList={loadingList}
          // workspaceList={workspaceList}
          onChangeViewFrm={value => setIsView(value)}
          setLoadingList={setLoadingList}
        />
      </ModalForm>
      <ModalForm
        isOpen={isOpenModalDelete}
        type="confirm"
        title={handleTitleDelete()}
        onCancel={() => {
          setIsOpenModalDelete(false);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isDeleting}
            disabled={isDeleting}
            htmlType="button"
            onClick={() => {
              setIsOpenModalDelete(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.delete")}
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              setIsDeleting(true);
              handleDelete(selectedChanel);
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {t("channel.deleteChannelMsg")}
        </div>
      </ModalForm>
    </div>
  );
};

const channelStyles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: "16px",
    marginBottom: "24px",
  },
};

export default ChannelPage;
